import { useMemo, useState } from "react";

import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { PAGE_SIZE } from "@shared/utils/constants";
import { MapPin } from "lucide-react";
import { useParams } from "react-router-dom";
import { useGetAssets, useUpdateIncident } from "../../hooks";
import FilterPanel from "./assetFilterPanel";

interface LinkAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  incidentId: string;
  incidentDetails: any;
}

interface AssetFilters {
  facility: string;
  system: string;
  network: string;
  latitude: 0;
  longitude: 0;
}

export const LinkAssetModal = ({
  isOpen,
  onClose,
  incidentId,
  incidentDetails,
}: LinkAssetModalProps) => {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { mutateAsync: updateIncident, isPending: isUpdating } =
    useUpdateIncident(id, true);
  const [filters, setFilters] = useState<AssetFilters>({
    facility: "all",
    system: "all",
    network: "all",
    latitude: 0,
    longitude: 0,
  });

  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    if (searchTerm.trim()) {
      params.search = searchTerm;
    }

    if (filters.facility !== "all") {
      params.facility = filters.facility;
    }
    if (filters.system !== "all") {
      params.system = filters.system;
    }
    if (filters.network !== "all") {
      params.network = filters.network;
    }
    if (filters.latitude && filters.longitude) {
      params.latitude = filters.latitude;
      params.longitude = filters.longitude;
    }

    return params;
  }, [currentPage, searchTerm, filters]);

  const { data: assetData } = useGetAssets(apiParams);

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleLinkAsset = (asset: any) => {
    updateIncident({ asset: asset.id });
    onClose();
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      facility: "all",
      system: "all",
      network: "all",
      latitude: 0,
      longitude: 0,
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getAssetsNearLocation = () => {
    console.log("Clicked! Updating filters with", {
      lat: incidentDetails.latitude,
      lng: incidentDetails.longitude,
    });
    setFilters({
      ...filters,
      latitude: incidentDetails.latitude,
      longitude: incidentDetails.longitude,
    });
    setCurrentPage(1); // Reset to first page when location search is triggered
  };

  // Extract data from API response
  const assets = assetData?.results || [];
  const totalItems = assetData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  // Pagination info
  const paginationInfo = {
    currentPage: currentPage,
    totalPages: totalPages,
    totalItems: totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
    endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
  };

  // Table Config
  const columns: TableColumn[] = [
    { key: "assetNo", header: "Asset ID", type: "text" },
    { key: "assetName", header: "Asset Name", type: "text" },
    {
      key: "assetTypeDisplay",
      header: "Asset Type",
      type: "text",
    },
    { key: "location", header: "Location/Network", type: "text" },
    { key: "distance", header: "Distance", type: "text" },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "Link",
      onClick: handleLinkAsset,
      icon: "Link",
      variant: "default",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link Asset to Incident {incidentId}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Asset Results Table */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex items-center gap-2"
              onClick={getAssetsNearLocation}
            >
              <MapPin className="w-4 h-4" />
              Find Assets Near Location
            </Button>
          </div>
          <GenericTable
            data={assets}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search by Asset ID or Name..."
            searchParamName="query"
            searchDebounceMs={300}
            searchValue={searchTerm}
            onSearch={handleSearch}
            actions={actions}
            actionsType="buttons"
            filterPanel={
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            }
            emptyMessage="No assets found"
            pagination={paginationInfo}
            onPageChange={handlePageChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
